import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTenant } from "../../context/TenantContext";
import AppointmentModal from "./AppointmentModal";
import DayView from "./DayView";
import SoapNoteModal from "../notes/SoapNoteModal";
import ContextMenu from "../common/ContextMenu";
import appointmentService from "../../services/appointmentService";
import clientService from "../../services/clientService";
import invoiceService from "../../services/invoiceService";
import { tenantDataService } from "../../services/tenantDataService";
import {
  FiChevronLeft,
  FiChevronRight,
  FiPlus,
  FiCalendar,
  FiClock,
  FiUser
} from 'react-icons/fi';
import toast from 'react-hot-toast';

// Function to generate time slots based on business hours
const generateTimeSlots = (startTime, endTime) => {
  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);

  const startSlot = startHour * 2 + (startMin >= 30 ? 1 : 0);
  const endSlot = endHour * 2 + (endMin > 30 ? 1 : 0);
  const slotCount = endSlot - startSlot;

  return Array.from({ length: slotCount }, (_, i) => {
    const totalSlot = startSlot + i;
    const hh = String(Math.floor(totalSlot / 2)).padStart(2, "0");
    const mm = totalSlot % 2 === 0 ? "00" : "30";
    return `${hh}:${mm}`;
  });
};

// Removed loadFromStorage function - now using data services

function getNoteStatus(appt, allNotes) {
  const note = allNotes.find(n => n.appointmentId === appt.id);
  if (!note) return "red";
  if (note.finalised) return "green";
  if (note.subjective || note.objective || note.assessment || note.plan) return "amber";
  return "red";
}
function getInvoiceStatus(appt, allInvoices) {
  const invoice = allInvoices.find(inv => inv.appointmentId === appt.id);
  if (!invoice) return "red";
  return invoice.status === "paid" ? "green" : "amber";
}
function apptBgColor(appt) {
  if (appt.status === "cancelled") return "bg-red-500";
  if (appt.status === "attended") return "bg-gray-400";
  if (appt.status === "dna") return "bg-orange-500";

  // Enhanced styling for different OOO event types
  if (appt.type === "ooo" || appt.type === "event") {
    // Leave requests - different colors based on status (muted tones)
    if (appt.eventType === "leave" || appt.eventType === "annual_leave") {
      return appt.status === "pending" ? "bg-amber-200" : "bg-emerald-200";
    }
    // Sick leave
    if (appt.eventType === "sick" || appt.eventType === "sick_leave") {
      return appt.status === "pending" ? "bg-orange-200" : "bg-red-200";
    }
    // Training/Conference
    if (appt.eventType === "training" || appt.eventType === "conference") {
      return "bg-indigo-600";
    }
    // Personal/Other
    if (appt.eventType === "personal" || appt.eventType === "other") {
      return "bg-purple-600";
    }
    // Block time / general OOO
    return "bg-teal-200";
  }

  return "bg-blue-500"; // Regular appointments
}

function apptTextColor(appt) {
  if (appt.status === "cancelled") return "text-white";
  if (appt.status === "attended") return "text-white";
  if (appt.status === "dna") return "text-white";

  // Enhanced text colors for different OOO event types
  if (appt.type === "ooo" || appt.type === "event") {
    // Light backgrounds need dark text
    if (appt.eventType === "leave" || appt.eventType === "annual_leave") {
      return "text-gray-800";
    }
    // Sick leave
    if (appt.eventType === "sick" || appt.eventType === "sick_leave") {
      return "text-gray-800";
    }
    // Training/Conference (dark background)
    if (appt.eventType === "training" || appt.eventType === "conference") {
      return "text-white";
    }
    // Personal/Other (dark background)
    if (appt.eventType === "personal" || appt.eventType === "other") {
      return "text-white";
    }
    // Block time / general OOO (light background)
    return "text-gray-800";
  }

  return "text-white"; // Regular appointments
}

function NoteStatus({ status, onClick, title }) {
  const map = {
    red: "bg-red-500",
    amber: "bg-yellow-400",
    green: "bg-green-500",
  };
  return (
    <span
      className={`inline-block w-3 h-3 rounded-full border border-white cursor-pointer ${map[status] || map.red} hover:scale-125 transition`}
      title={title}
      onClick={e => {
        e.stopPropagation();
        if (onClick) onClick();
      }}
      tabIndex={0}
      role={onClick ? "button" : undefined}
    ></span>
  );
}

export default function CalendarView({
  filterStaff = "all",
  filterRoom = "all",
  filterType = "all",
  shouldOpenModal = false,
  modalType = null,
  onModalOpened = () => { }
}) {
  const { currentTenant } = useTenant();
  const location = useLocation();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [showSoapModal, setShowSoapModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [contextMenu, setContextMenu] = useState(null);

  // Keyboard shortcuts handler
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle shortcuts when no modal is open and not typing in input fields
      if (showModal || showSoapModal || event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
        return;
      }

      switch (event.key.toLowerCase()) {
        case 'n':
          // New appointment - open modal for current date
          event.preventDefault();
          setSelectedSlot({ date: selectedDate, hour: "09:00" });
          setEditingAppointment(null);
          setShowModal(true);
          break;
        case 'escape':
          // Close any open modals
          event.preventDefault();
          setShowModal(false);
          setShowSoapModal(false);
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showModal, showSoapModal, selectedDate]);
  const [soapNotes, setSoapNotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [businessHours, setBusinessHours] = useState(null);
  const [hours, setHours] = useState([]);

  // Missing state variables
  const [anchor, setAnchor] = useState(new Date());
  const [view, setView] = useState("week");
  const [modalOpen, setModalOpen] = useState(false);
  const [soapModalOpen, setSoapModalOpen] = useState(false);
  const [soapAppointment, setSoapAppointment] = useState(null);
  const [soapClient, setSoapClient] = useState(null);
  const [notes, setNotes] = useState([]);
  const [waitlistData, setWaitlistData] = useState(null);

  // Load all data when tenant context is ready
  useEffect(() => {
    if (currentTenant) {
      loadAllData();
      loadBusinessHours();
    }
  }, [currentTenant]);

  // Handle waitlist conversion from navigation state
  useEffect(() => {
    if (location.state?.openAppointmentModal && location.state?.waitlistData) {
      setWaitlistData(location.state.waitlistData);
      setShowModal(true);
      setModalOpen(true);
    }
  }, [location.state]);

  // Handle pending appointment from booking request approval
  const openedRef = useRef(false);

  // Define what "loaded" means for the calendar
  const isLoaded = !loading && currentTenant && appointments.length >= 0 && clients.length >= 0;

  function safeParse(raw) {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  useEffect(() => {
    if (openedRef.current) return;      // already opened once

    const qs = new URLSearchParams(location.search);

    // ACCEPT BOTH styles (+ legacy openDialog=true)
    const wantsAdd =
      (qs.get("action") === "add" && qs.get("type") === "appointment") ||
      qs.get("action") === "approve-booking" ||
      qs.get("openDialog") === "true";

    // Enhanced diagnostic logging - ALWAYS run this for debugging
    console.log("🚀 BOOKING APPROVAL DEBUG:");
    console.log("[calendar] isLoaded:", isLoaded);
    console.log("[calendar] currentTenant:", !!currentTenant);
    console.log("[calendar] localStorage present?", !!localStorage.getItem("pendingAppointment_v1"));
    console.log("[calendar] sessionStorage present?", !!sessionStorage.getItem("pendingAppointment_v1"));
    console.log("[calendar] url", location.search);
    console.log("[calendar] wantsAdd:", wantsAdd);

    // Don't wait for full data loading for booking approvals - just need basic tenant context
    if (!currentTenant && wantsAdd) {
      console.log("⏳ Waiting for tenant context...");
      return;
    }

    // Read from localStorage first, then sessionStorage for cross-tab compatibility
    const raw =
      localStorage.getItem("pendingAppointment_v1") ??
      sessionStorage.getItem("pendingAppointment_v1") ??
      sessionStorage.getItem("bookingRequest"); // legacy

    const payload = raw ? safeParse(raw) : null;

    console.debug("🎯 CalendarView: Modal trigger check:", {
      shouldOpenModal: Boolean(wantsAdd && payload),
      modalType: wantsAdd ? "appointment" : null,
      hasBookingRequest: Boolean(payload),
      url: location.search,
      wantsAdd,
      hasPayload: !!payload,
      rawData: raw ? "FOUND" : "MISSING"
    });

    if (!wantsAdd) {
      console.log("❌ URL doesn't match booking approval pattern");
      return;
    }

    if (!payload) {
      console.log("❌ No booking payload found in storage");
      return;
    }

    console.log('📅 FORCE OPENING appointment dialog with booking request data:', payload);

    // Set the selected date to the appointment date
    if (payload.date) {
      setSelectedDate(new Date(payload.date));
    }

    // === FORCE OPEN THE DIALOG HERE ===
    setWaitlistData(payload);
    setShowModal(true);
    setModalOpen(true);

    openedRef.current = true;

    // Clean up ONLY AFTER opening - remove from both storage types
    localStorage.removeItem("pendingAppointment_v1");
    sessionStorage.removeItem("pendingAppointment_v1");
    sessionStorage.removeItem("bookingRequest"); // legacy

    // Strip params so refresh doesn't reopen
    const newUrl = new URL(window.location.href);
    ["action", "type", "source", "openDialog"].forEach(k =>
      newUrl.searchParams.delete(k)
    );
    window.history.replaceState({}, "", newUrl.toString());

    console.log('✅ Booking approval modal FORCE OPENED and cleaned up');
  }, [currentTenant, location.search]); // Changed dependency to currentTenant instead of isLoaded


  // Handle automatic modal opening for leave requests
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hasBookingRequest = urlParams.get('openDialog') === 'true' && sessionStorage.getItem('pendingAppointment');

    console.log('🎯 CalendarView: Modal trigger check:', { shouldOpenModal, modalType, hasBookingRequest });

    // Don't interfere if we have a booking request to process
    if (hasBookingRequest) {
      console.log('🎯 CalendarView: Skipping leave modal - booking request takes priority');
      return;
    }

    if (shouldOpenModal && modalType === 'leave') {
      console.log('🎯 CalendarView: Opening leave modal automatically');

      // Create a slot for current time to pre-populate the modal
      const now = new Date();
      const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(Math.floor(now.getMinutes() / 30) * 30).padStart(2, '0')}`;

      const slotData = {
        date: now.toISOString().split('T')[0],
        time: currentTime,
        type: 'leave'
      };

      console.log('🎯 CalendarView: Setting slot data:', slotData);

      setSelectedSlot(slotData);
      setShowModal(true);
      setModalOpen(true);
      onModalOpened();
    }
  }, [shouldOpenModal, modalType, onModalOpened]);

  // Callback to refresh waitlist after appointment creation
  const handleWaitlistRefresh = () => {
    // Navigate back to waitlist to trigger refresh
    if (location.state?.openAppointmentModal && location.state?.waitlistData) {
      // Use a timeout to ensure the appointment is fully saved before navigating
      setTimeout(() => {
        window.location.href = '/waitlist';
      }, 500);
    }
  };

  // Load business hours and generate time slots
  const loadBusinessHours = async () => {
    if (!currentTenant?.id) return;

    try {
      tenantDataService.setTenantContext(currentTenant.id);
      const settingsDoc = await tenantDataService.getById('settings', 'clinic');

      if (settingsDoc && settingsDoc.businessHours) {
        setBusinessHours(settingsDoc.businessHours);
        updateTimeSlots(settingsDoc.businessHours, selectedDate);
      } else {
        // Fallback to default business hours
        const defaultHours = {
          monday: { open: '08:00', close: '18:00', closed: false },
          tuesday: { open: '08:00', close: '18:00', closed: false },
          wednesday: { open: '08:00', close: '18:00', closed: false },
          thursday: { open: '08:00', close: '18:00', closed: false },
          friday: { open: '08:00', close: '18:00', closed: false },
          saturday: { open: '09:00', close: '13:00', closed: true },
          sunday: { open: '09:00', close: '17:00', closed: true }
        };
        setBusinessHours(defaultHours);
        updateTimeSlots(defaultHours, selectedDate);
      }
    } catch (error) {
      console.error('Error loading business hours:', error);
      // Fallback to default business hours
      setHours(generateTimeSlots('08:00', '17:00'));
    }
  };

  // Update time slots based on business hours and selected date
  const updateTimeSlots = (businessHoursData, date) => {
    if (!businessHoursData) {
      setHours(generateTimeSlots('08:00', '17:00'));
      return;
    }

    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    const dayHours = businessHoursData[dayName];

    if (dayHours && !dayHours.closed) {
      const timeSlots = generateTimeSlots(dayHours.open, dayHours.close);
      setHours(timeSlots);
    } else {
      // Always show some hours even for closed days to prevent empty calendar
      setHours(generateTimeSlots('08:00', '17:00'));
    }
  };

  // Update time slots when selected date changes
  useEffect(() => {
    if (businessHours && selectedDate) {
      updateTimeSlots(businessHours, selectedDate);
    }
  }, [selectedDate, businessHours]);

  const loadAllData = async () => {
    try {
      console.log('🔄 CalendarView: Loading all data...');
      const [appointmentsData, clientsData, invoicesData] = await Promise.all([
        appointmentService.getAllAppointments(),
        clientService.getAllClients(),
        invoiceService.getAllInvoices()
      ]);

      console.log(`📅 CalendarView: Loaded ${appointmentsData.length} appointments`);
      console.log('📅 CalendarView: Appointment data:', appointmentsData);

      setAppointments(appointmentsData);
      setClients(clientsData);
      setInvoices(invoicesData);

      // Load SOAP notes from Firebase
      const { getNotes } = await import('../../services/noteService');
      const notesData = await getNotes();
      setSoapNotes(notesData);
      setNotes(notesData);
    } catch (error) {
      console.error('Error loading calendar data:', error);
    }
  };



  // Updated: "reloadAll" can now be called from modal "Save" as well as modal close
  const reloadAll = useCallback(() => {
    loadAllData();
  }, []);

  const weekDays = useMemo(() => {
    const d = new Date(anchor);
    const dayNum = d.getDay();
    const monday = new Date(d);
    monday.setDate(d.getDate() - ((dayNum + 6) % 7));
    return Array.from({ length: 7 }, (_, i) => {
      const dd = new Date(monday);
      dd.setDate(monday.getDate() + i);
      return dd;
    });
  }, [anchor]);

  const prev = () => {
    const d = new Date(anchor);
    if (view === "day") d.setDate(d.getDate() - 1);
    else if (view === "week") d.setDate(d.getDate() - 7);
    else d.setMonth(d.getMonth() - 1);
    setAnchor(d);
  };
  const next = () => {
    const d = new Date(anchor);
    if (view === "day") d.setDate(d.getDate() + 1);
    else if (view === "week") d.setDate(d.getDate() + 7);
    else d.setMonth(d.getMonth() + 1);
    setAnchor(d);
  };

  const openSlot = ({ date, hour, startIndex }) => {
    setSelectedSlot({ date, hour, startIndex });
    setSelectedAppointment(null);
    setModalOpen(true);
  };
  const editAppt = (appt, slot) => {
    setSelectedAppointment(appt);
    setSelectedSlot(slot);
    setModalOpen(true);
  };
  const handleModalClose = () => {
    setModalOpen(false);
    setShowModal(false);
    setSelectedSlot(null);
    setSelectedAppointment(null);
    setWaitlistData(null);
    setTimeout(reloadAll, 50);
  };

  // --- CLICKABLE DOT HANDLERS ---
  const handleSoapDotClick = (appt) => {
    const client = clients.find(c => c.id === appt.clientId);
    if (client) {
      setSoapAppointment(appt);
      setSoapClient(client);
      setSoapModalOpen(true);
    }
  };
  const handleInvoiceDotClick = (appt) => {
    window.location.href = `/clients/${appt.clientId}?tab=billing&appt=${appt.id}`;
  };

  // Get SOAP note status for appointment
  const getNoteStatus = (appointment, notes) => {
    const appointmentNotes = notes.filter(note => note.appointmentId === appointment.id);
    if (appointmentNotes.length === 0) return "red"; // No notes created

    const hasFinalized = appointmentNotes.some(note => note.finalised === true);
    if (hasFinalized) return "green"; // Has finalized note

    return "amber"; // Has draft notes but not finalized
  };

  // Get invoice status for appointment
  const getInvoiceStatus = (appointment, invoices) => {
    const appointmentInvoices = invoices.filter(invoice =>
      invoice.appointmentId === appointment.id ||
      (invoice.clientId === appointment.clientId &&
        invoice.appointmentDate === appointment.date &&
        invoice.appointmentType === appointment.type)
    );

    if (appointmentInvoices.length === 0) return "red"; // No invoice created

    const paidInvoice = appointmentInvoices.find(invoice => invoice.status === "paid");
    if (paidInvoice) return "green"; // Invoice paid

    return "amber"; // Invoice created but not paid
  };

  const handleSoapModalClose = () => {
    setSoapModalOpen(false);
    setSoapAppointment(null);
    setSoapClient(null);
    // Reload notes after SOAP note changes
    setTimeout(async () => {
      try {
        const { getNotes } = await import('../../services/noteService');
        const notesData = await getNotes();
        setSoapNotes(notesData);
        setNotes(notesData);
      } catch (error) {
        console.error('Error reloading notes:', error);
      }
    }, 100);
  };

  const handleNoteSaved = async () => {
    // Reload notes when a SOAP note is saved
    try {
      const { getNotes } = await import('../../services/noteService');
      const notesData = await getNotes();
      setSoapNotes(notesData);
      setNotes(notesData);
    } catch (error) {
      console.error('Error reloading notes:', error);
    }
  };

  const handleNoteDeleted = async () => {
    // Reload notes when a SOAP note is deleted
    try {
      const { getNotes } = await import('../../services/noteService');
      const notesData = await getNotes();
      setSoapNotes(notesData);
      setNotes(notesData);
    } catch (error) {
      console.error('Error reloading notes:', error);
    }
  };

  const filteredAppointments = useMemo(
    () => {
      console.log(`🔍 CalendarView: Filtering ${appointments.length} appointments with ${clients.length} clients`);

      // First enrich appointments with client data and filter out corrupted appointments
      const enrichedAppointments = appointments
        .filter(appt => {
          // Filter out Firebase initialization documents
          if (appt.id === '_init' || appt._initialized === true) {
            console.log(`🔧 Filtered out initialization document:`, appt.id);
            return false;
          }
          return true;
        })
        .map(appt => {
          // Check for corrupted appointment data
          if (!appt.date || !appt.time) {
            console.warn(`❌ Corrupted appointment found (missing date/time):`, appt);
            return null;
          }

          // Check for invalid date
          try {
            const dateObj = new Date(appt.date);
            if (isNaN(dateObj.getTime())) {
              console.warn(`❌ Corrupted appointment found (invalid date):`, appt);
              return null;
            }
          } catch (error) {
            console.warn(`❌ Corrupted appointment found (date parse error):`, appt);
            return null;
          }

          // Handle OOO events (no client required) vs appointments (client required)
          if (appt.type === 'ooo' || appt.type === 'event') {
            // OOO events and other events don't require a client
            console.log(`✅ Processing OOO/event appointment:`, appt.id);
            return { ...appt, client: null };
          } else {
            // Regular appointments require a client
            const client = clients.find(c => c.id === appt.clientId);
            if (!client) {
              console.warn(`❌ Orphaned appointment found with missing client ID: ${appt.clientId}`, appt);
              return null; // Filter out appointments with missing clients
            }
            console.log(`✅ Processing regular appointment with client:`, appt.id, client.name);
            return { ...appt, client };
          }
        })
        .filter(Boolean); // Remove null entries

      console.log(`📋 CalendarView: After enrichment, ${enrichedAppointments.length} valid appointments`);

      // Apply user filters (staff, room, type)
      const finalFiltered = enrichedAppointments.filter(appt =>
        (filterStaff === 'all' || appt.staffId === filterStaff) &&
        (filterRoom === 'all' || appt.roomId === filterRoom) &&
        (filterType === 'all' || appt.type === filterType)
      );

      console.log(`🎯 CalendarView: After filters, ${finalFiltered.length} appointments to display`);
      return finalFiltered;
    },
    [appointments, clients, filterStaff, filterRoom, filterType]
  );

  return (
    <div className="space-y-6">
      {/* MODERN HEADER */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          {/* View Toggle */}
          <div className="flex items-center bg-gray-50 rounded-lg p-1">
            {["day", "week", "month"].map(v => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${view === v
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  }`}
              >
                {v[0].toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>

          {/* Add Appointment Button */}
          <button
            onClick={() =>
              openSlot({
                date: anchor,
                hour: "06:00",
                startIndex: hours.indexOf("06:00"),
              })
            }
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 shadow-sm"
          >
            <FiPlus className="w-4 h-4" />
            Add Appointment
          </button>
        </div>

        {/* Navigation and Date Display */}
        <div className="flex items-center justify-between">
          <button
            onClick={prev}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors duration-200"
          >
            <FiChevronLeft className="w-4 h-4" />
            Previous
          </button>

          <div className="flex items-center gap-3">
            <FiCalendar className="w-5 h-5 text-gray-400" />
            <h2 className="text-xl font-semibold text-gray-900">
              {view === "day"
                ? anchor.toLocaleDateString("en-GB", {
                  weekday: "long",
                  day: "2-digit",
                  month: "long",
                  year: "numeric",
                })
                : view === "week"
                  ? `${weekDays[0].toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                  })} — ${weekDays[6].toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}`
                  : anchor.toLocaleDateString(undefined, {
                    month: "long",
                    year: "numeric",
                  })}
            </h2>
          </div>

          <button
            onClick={next}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors duration-200"
          >
            Next
            <FiChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* BODY */}
      {view === "day" ? (
        <DayView
          appointments={filteredAppointments}
          hours={hours.length > 0 ? hours : generateTimeSlots('08:00', '17:00')}
          clients={clients}
          notes={notes}
          invoices={invoices}
          onSlotClick={({ hour, startIndex }) =>
            openSlot({ date: anchor, hour, startIndex })
          }
          onEdit={editAppt}
          date={anchor}
          getNoteStatus={(appt) => getNoteStatus(appt, notes)}
          getInvoiceStatus={(appt) => getInvoiceStatus(appt, invoices)}
          handleSoapDotClick={handleSoapDotClick}
          handleInvoiceDotClick={handleInvoiceDotClick}
        />
      ) : view === "week" ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="grid grid-cols-8">
            <div className="bg-gray-50 p-4 font-semibold text-center text-gray-700 border-r border-gray-200">
              <FiClock className="w-4 h-4 mx-auto mb-1" />
              Time
            </div>
            {weekDays.map(d => {
              const isToday = d.toDateString() === new Date().toDateString();
              return (
                <div
                  key={d.toISOString()}
                  className={`p-4 font-semibold text-center border-r border-gray-200 last:border-r-0 ${isToday
                    ? "bg-blue-50 text-blue-700"
                    : "bg-gray-50 text-gray-700"
                    }`}
                >
                  <div className="text-xs text-gray-500 mb-1">
                    {d.toLocaleDateString("en-GB", { weekday: "short" })}
                  </div>
                  <div className={`text-lg font-bold ${isToday ? "text-blue-600" : "text-gray-900"
                    }`}>
                    {d.getDate()}
                  </div>
                  <div className="text-xs text-gray-500">
                    {d.toLocaleDateString("en-GB", { month: "short" })}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-8">
            {(hours.length > 0 ? hours : generateTimeSlots('08:00', '17:00')).map((hour, i) => (
              <React.Fragment key={hour}>
                <div
                  className="border-r border-gray-200 bg-gray-50 text-center text-sm text-gray-600 flex items-center justify-center"
                  style={{ height: 40 }}
                >
                  {i % 2 === 0 ? (
                    <span className="font-medium">{hour}</span>
                  ) : (
                    <span className="text-gray-400">·</span>
                  )}
                </div>
                {weekDays.map(d => {
                  const slotAppts = filteredAppointments.filter(
                    a => {
                      const currentDate = new Date(d);
                      const appointmentStartDate = new Date(a.date);

                      // Debug logging for week view
                      if (i === 0 && d.getDate() === new Date().getDate()) { // Only log for today's column, first hour
                        console.log(`🔍 WeekView Debug: Filtering appointments for ${d.toDateString()}`);
                        console.log(`📅 Sample appointment: ${a.date} (${appointmentStartDate.toDateString()}) at ${a.time}`);
                        console.log(`📅 Current date: ${currentDate.toDateString()}`);
                      }

                      const isAllDayEvent = (a.type === 'ooo' || a.type === 'event') &&
                        (a.allDay || (a.time === '00:00' && a.endTime === '23:59'));

                      if (isAllDayEvent) {
                        // For multi-day events, check if current date falls within the range
                        const appointmentEndDate = a.endDate ? new Date(a.endDate) : appointmentStartDate;

                        // Normalize dates to midnight for proper comparison
                        const currentDateNormalized = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
                        const startDateNormalized = new Date(appointmentStartDate.getFullYear(), appointmentStartDate.getMonth(), appointmentStartDate.getDate());
                        const endDateNormalized = new Date(appointmentEndDate.getFullYear(), appointmentEndDate.getMonth(), appointmentEndDate.getDate());

                        const isWithinRange = currentDateNormalized >= startDateNormalized && currentDateNormalized <= endDateNormalized;
                        return isWithinRange && i === 0; // Show only in first time slot but will span full height
                      }

                      // For time-specific events, match the exact time and date
                      const dateMatches = appointmentStartDate.toDateString() === currentDate.toDateString();
                      const timeMatches = a.time === hour;

                      if (i === 0 && d.getDate() === new Date().getDate()) { // Debug for today
                        console.log(`🎯 Date matches: ${dateMatches}, Time matches (${a.time} === ${hour}): ${timeMatches}`);
                      }

                      return dateMatches && timeMatches;
                    }
                  );
                  const isToday = d.toDateString() === new Date().toDateString();
                  return (
                    <div
                      key={`${d.toISOString()}-${hour}`}
                      className={`border-r border-b border-gray-200 last:border-r-0 relative cursor-pointer transition-colors duration-150 ${isToday
                        ? "hover:bg-blue-50"
                        : "hover:bg-gray-50"
                        }`}
                      style={{ height: 40 }}
                      onClick={() =>
                        openSlot({ date: d, hour, startIndex: i })
                      }
                    >
                      {slotAppts.map((appt, idx) => {
                        // Check if this is an all-day event
                        const isAllDayEvent = (appt.type === 'ooo' || appt.type === 'event') &&
                          (appt.allDay || (appt.time === '00:00' && appt.endTime === '23:59'));

                        // Calculate height - all-day events span the full day
                        const safeHours = hours.length > 0 ? hours : generateTimeSlots('08:00', '17:00');
                        const span = isAllDayEvent ? safeHours.length : Math.ceil(appt.duration / 30);
                        const blockHeight = isAllDayEvent ? safeHours.length * 40 + (safeHours.length - 1) : span * 40 + (span - 1);
                        const noteStatus = getNoteStatus(appt, notes);
                        const invoiceStatus = getInvoiceStatus(appt, invoices);

                        return (
                          <div
                            key={appt.id}
                            className={`absolute top-0 ${apptTextColor(appt)} text-xs p-2 rounded-lg shadow-sm flex flex-col items-start transition-all duration-200 hover:shadow-md ${apptBgColor(appt)} border border-white/20 ${(appt.type === 'ooo' || appt.type === 'event') ? 'border-2 border-gray-300/50' : ''}`}
                            style={{
                              height: `${blockHeight}px`,
                              width: `${100 / slotAppts.length}%`,
                              left: `${(100 / slotAppts.length) * idx}%`,
                              zIndex: 20,
                            }}
                            onClick={e => {
                              e.stopPropagation();
                              editAppt(appt, { date: d, hour, startIndex: i });
                            }}
                          >
                            <div className={`font-semibold ${apptTextColor(appt)} text-xs leading-tight mb-1`}>
                              {appt.type === 'ooo' || appt.type === 'event' ? (
                                <>
                                  <div className="flex items-center gap-1">
                                    <FiClock className="w-3 h-3 flex-shrink-0" />
                                    <span className="truncate font-bold">{appt.title || appt.eventType || 'OOO Event'}</span>
                                    {appt.status === 'pending' && (
                                      <span className="text-[9px] bg-gray-700/20 px-1 rounded text-gray-700 font-medium">PENDING</span>
                                    )}
                                  </div>
                                </>
                              ) : (
                                <>
                                  <FiUser className="w-3 h-3 inline mr-1" />
                                  {appt.client?.name || "Unknown"}
                                </>
                              )}
                            </div>
                            <div className="text-white text-[10px] leading-tight opacity-90">
                              {appt.type === 'ooo' || appt.type === 'event' ? (
                                <div className="flex items-center justify-between">
                                  <span>{appt.allDay ? 'All Day' : `${appt.duration || 60}min`}</span>
                                  {appt.eventType && (
                                    <span className="text-[9px] bg-white/20 px-1 rounded uppercase font-medium">
                                      {appt.eventType.replace('_', ' ')}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                `${appt.type} • ${appt.duration}min`
                              )}
                            </div>
                            <div className="flex items-center gap-1 mt-1">
                              {appt.status === "cancelled" && (
                                <span title="Cancelled" className="text-xs">✖</span>
                              )}
                              {appt.status === "declined" && (
                                <span title="Declined" className="text-xs">✖</span>
                              )}
                              {appt.status === "dna" && (
                                <span title="Did Not Attend" className="text-[9px] bg-white/20 px-1 rounded">DNA</span>
                              )}
                              {appt.status === "attended" && (
                                <span title="Attended" className="text-xs">✔</span>
                              )}
                              {/* Only show SOAP and invoice status for regular appointments, not OOO events */}
                              {appt.type !== 'ooo' && appt.type !== 'event' && (
                                <>
                                  <NoteStatus
                                    status={noteStatus}
                                    onClick={() => handleSoapDotClick(appt)}
                                    title={
                                      noteStatus === "green"
                                        ? "SOAP note complete"
                                        : noteStatus === "amber"
                                          ? "SOAP note in progress"
                                          : "Create SOAP note"
                                    }
                                  />
                                  <NoteStatus
                                    status={invoiceStatus}
                                    onClick={() => handleInvoiceDotClick(appt)}
                                    title={
                                      invoiceStatus === "green"
                                        ? "Invoice paid"
                                        : invoiceStatus === "red"
                                          ? "Invoice created - unpaid"
                                          : "Create invoice"
                                    }
                                  />
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      ) : (
        // ——— MODERN MONTH VIEW ———
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="grid grid-cols-7 bg-gray-50">
            {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((dow, idx) => {
              const shortDay = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][idx];
              return (
                <div
                  key={dow}
                  className="p-4 font-semibold text-center border-r border-gray-200 last:border-r-0"
                >
                  <div className="text-gray-700 text-sm">{shortDay}</div>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-7">
            {Array.from({ length: 6 }).flatMap((_, row) =>
              Array.from({ length: 7 }).map((_, col) => {
                const idx = row * 7 + col;
                const first = new Date(
                  anchor.getFullYear(),
                  anchor.getMonth(),
                  1
                );
                const offset = (first.getDay() + 6) % 7; // Mon=0
                const dayNum = idx - offset + 1;
                const cellD = new Date(
                  anchor.getFullYear(),
                  anchor.getMonth(),
                  dayNum
                );
                const inMonth = cellD.getMonth() === anchor.getMonth();
                const dayAppts = filteredAppointments.filter(
                  a =>
                    new Date(a.date).toDateString() ===
                    cellD.toDateString()
                );
                const isToday = cellD.toDateString() === new Date().toDateString();
                return (
                  <div
                    key={cellD.toISOString()}
                    className={`h-32 p-3 relative cursor-pointer border-r border-b border-gray-200 last:border-r-0 transition-colors duration-150 ${inMonth
                      ? isToday
                        ? "bg-blue-50 hover:bg-blue-100"
                        : "hover:bg-gray-50"
                      : "bg-gray-50"
                      }`}
                    onClick={() => {
                      // Navigate to day view for the clicked date
                      setView("day");
                      setAnchor(new Date(cellD));
                    }}
                    onContextMenu={e => {
                      e.preventDefault();
                      setContextMenu({
                        x: e.clientX,
                        y: e.clientY,
                        slot: { date: cellD, hour: "09:00" }
                      });
                    }}
                  >
                    {inMonth ? (
                      <>
                        <div className={`font-semibold text-sm mb-2 ${isToday
                          ? "text-blue-600 bg-blue-100 w-6 h-6 rounded-full flex items-center justify-center"
                          : "text-gray-900"
                          }`}>
                          {cellD.getDate()}
                        </div>
                        <div className="space-y-1">
                          {dayAppts.slice(0, 3).map(appt => (
                            <div
                              key={appt.id}
                              className={`rounded-md text-xs px-2 py-1 truncate font-medium transition-all duration-200 hover:shadow-sm cursor-pointer ${apptBgColor(appt)} text-white border border-white/20 ${(appt.type === 'ooo' || appt.type === 'event') ? 'border-2 border-gray-300/50 shadow-md' : ''}`}
                              onClick={e => {
                                e.stopPropagation();
                                editAppt(appt, {
                                  date: cellD,
                                  hour: appt.hour,
                                  startIndex: appt.startIndex,
                                });
                              }}
                              onContextMenu={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                setContextMenu({
                                  x: e.clientX,
                                  y: e.clientY,
                                  appointment: appt
                                });
                              }}
                              title={
                                appt.type === 'ooo' || appt.type === 'event' ?
                                  `${appt.title || appt.eventType || 'OOO Event'} @ ${appt.time}` +
                                  (appt.status === "cancelled" ? " (Cancelled)" : "")
                                  :
                                  `${appt.client?.name || "Unknown"} @ ${appt.time}` +
                                  (appt.status === "cancelled" ? " (Cancelled)" : appt.status === "dna" ? " (DNA)" : appt.status === "attended" ? " (Attended)" : "")
                              }
                            >
                              <div className="flex items-center justify-between gap-1">
                                <div className="flex items-center gap-1 flex-1 min-w-0">
                                  {appt.type === 'ooo' || appt.type === 'event' ? (
                                    <>
                                      <FiClock className="w-3 h-3 flex-shrink-0" />
                                      <span className="truncate font-semibold">{appt.title || appt.eventType || 'OOO Event'}</span>
                                      {appt.status === "cancelled" && <span className="text-xs">✖</span>}
                                      {appt.status === 'pending' && (
                                        <span className="text-[8px] bg-white/30 px-1 rounded font-medium">PENDING</span>
                                      )}
                                      {appt.eventType && appt.eventType !== (appt.title || 'OOO Event') && (
                                        <span className="text-[8px] bg-white/20 px-1 rounded uppercase">
                                          {appt.eventType.replace('_', ' ')}
                                        </span>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      <FiUser className="w-3 h-3 flex-shrink-0" />
                                      <span className="truncate">{appt.client?.name || "Unknown"}</span>
                                      {appt.status === "cancelled" && <span className="text-xs">✖</span>}
                                      {appt.status === "dna" && <span className="text-[9px] bg-white/20 px-1 rounded">DNA</span>}
                                      {appt.status === "attended" && <span className="text-xs">✔</span>}
                                    </>
                                  )}
                                </div>
                                {/* Only show SOAP and invoice status for regular appointments, not OOO events */}
                                {appt.type !== 'ooo' && appt.type !== 'event' && (
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    <NoteStatus
                                      status={getNoteStatus(appt, notes)}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSoapDotClick(appt);
                                      }}
                                      title={
                                        getNoteStatus(appt, notes) === "green"
                                          ? "View finalised SOAP note"
                                          : getNoteStatus(appt, notes) === "amber"
                                            ? "Edit SOAP note"
                                            : "Create SOAP note"
                                      }
                                    />
                                    <NoteStatus
                                      status={getInvoiceStatus(appt, invoices)}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleInvoiceDotClick(appt);
                                      }}
                                      title={
                                        getInvoiceStatus(appt, invoices) === "green"
                                          ? "Invoice paid"
                                          : getInvoiceStatus(appt, invoices) === "amber"
                                            ? "Invoice created - unpaid"
                                            : "Create invoice"
                                      }
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                          {dayAppts.length > 3 && (
                            <div className="text-xs text-gray-500 font-medium px-2">
                              +{dayAppts.length - 3} more
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="absolute inset-0 bg-gray-100" />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {modalOpen && (selectedSlot || waitlistData) && (
        <AppointmentModal
          slot={selectedSlot}
          existing={selectedAppointment}
          onClose={handleModalClose}
          onSaved={reloadAll}  // <-- this prop enables the modal to trigger calendar update
          onDelete={reloadAll}  // <-- handle appointment deletion
          clients={clients}
          appointments={appointments}
          hours={hours}
          waitlistData={waitlistData}
          onWaitlistRefresh={handleWaitlistRefresh}
        />
      )}

      {soapModalOpen && soapAppointment && soapClient && (
        <SoapNoteModal
          appointment={soapAppointment}
          client={soapClient}
          onClose={handleSoapModalClose}
          onNoteSaved={handleNoteSaved}
          onNoteDeleted={handleNoteDeleted}
        />
      )}

      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          appointment={contextMenu.appointment}
          slot={contextMenu.slot}
          onClose={() => setContextMenu(null)}
          onNewAppointment={(slot) => {
            setSelectedSlot(slot);
            setEditingAppointment(null);
            setShowModal(true);
          }}
          onEditAppointment={(appointment) => {
            setEditingAppointment(appointment);
            setShowModal(true);
          }}
          onDeleteAppointment={(appointment) => {
            // Add delete confirmation logic here
            console.log('Delete appointment:', appointment);
          }}
          onCopyAppointment={(appointment) => {
            // Add copy logic here
            console.log('Copy appointment:', appointment);
          }}
        />
      )}
    </div>
  );
}
